import {
  GrabIconButton,
  Stack,
  Typography,
  DeleteIconButton,
  Box,
  DELETE_WARNING,
  Checkbox,
  RichTextEditor,
  RichTextReader,
  enumContains,
  useDebounce,
} from "@compass/core";
import { FormAnswer, FormQuestion, SurveyViewResource } from "generated/openApi";
import produce from "immer";
import { useEffect, useState } from "react";
import { Input, Select } from "components";
import { Draggable } from "react-beautiful-dnd";
import { BranchableFormQuestionTypes } from "./SurveyQuestionEnum";

type SurveyQuestionAnswerProps = {
  answer: FormAnswer;
  index: number;
  updateAnswer: (value: FormAnswer) => void;
  selected: boolean | undefined;
  branchableQuestions: FormQuestion[];
  isBranchable: boolean;
  viewResource: SurveyViewResource;
};

export const SurveyQuestionAnswer = ({
  answer,
  index,
  updateAnswer,
  selected,
  branchableQuestions,
  isBranchable,
  viewResource,
}: SurveyQuestionAnswerProps) => {
  const [formAnswer, setFormAnswer] = useState<FormAnswer | undefined>(answer);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const debouncedFormAnswer = useDebounce(formAnswer, 150);

  useEffect(() => {
    if (debouncedFormAnswer) updateAnswer(debouncedFormAnswer);
  }, [debouncedFormAnswer]);

  const grid = 8;

  const getItemStyle = (isDragging, draggableStyle) => ({
    // some basic styles to make the items look a bit nicer
    userSelect: "none",
    padding: grid * 1,
    margin: `0 0 ${grid}px 0`,

    // styles we need to apply on draggables
    ...draggableStyle,
  });

  const deleteAnswer = (e) => {
    if (window.confirm(DELETE_WARNING)) {
      e.stopPropagation();
      setFormAnswer(
        produce((draft) => {
          draft!.deleted = true;
        })
      );
    }
  };

  return (
    <Draggable
      key={`${formAnswer?.id}:${formAnswer?.displayOrder}`}
      draggableId={`${formAnswer?.id}:${formAnswer?.displayOrder}`}
      index={index || 0}
    >
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
        >
          <Box onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
            <Stack direction="row" spacing="sm" alignItems="center">
              <Box style={{ minWidth: 300 }}>
                {!selected && <RichTextReader html={formAnswer?.name} />}
                {selected && (
                  <RichTextEditor
                    justStyle
                    placeholder="Add New Answer"
                    value={formAnswer?.name}
                    onChange={(value) =>
                      setFormAnswer(
                        produce((draft) => {
                          draft!.name = value!;
                        })
                      )
                    }
                  />
                )}
              </Box>
              {isBranchable && selected && (
                <Box style={{ minWidth: 180 }}>
                  <Select
                    label="Branch Question"
                    value={branchableQuestions?.find((x) => x.tempId == formAnswer?.branchToFormQuestionTempId)}
                    options={branchableQuestions}
                    getOptionLabel={(option) => option.label || "No Label"}
                    onChange={(e, value) =>
                      setFormAnswer(
                        produce((draft) => {
                          draft!.branchToFormQuestionTempId = value?.tempId;
                        })
                      )
                    }
                  />
                </Box>
              )}
              {selected && !isHovered! && formAnswer?.isOther && (
                <Stack direction="row" style={{ minWidth: 200 }}>
                  {formAnswer?.isOther && (
                    <Select
                      required
                      label="Other Write-In Type"
                      value={viewResource?.formQuestionWriteInTypes?.find(
                        (x) => x.id === formAnswer?.otherWriteInTypeId
                      )}
                      options={viewResource?.formQuestionWriteInTypes}
                      getOptionLabel={(option) => option.name}
                      onChange={(e, value) =>
                        setFormAnswer(
                          produce((draft) => {
                            draft!.otherWriteInTypeId = value?.id;
                          })
                        )
                      }
                    />
                  )}
                </Stack>
              )}
              <Box {...provided.dragHandleProps} style={{ minWidth: 400 }}>
                {selected && isHovered && answer && (
                  <Stack direction="row">
                    <Checkbox
                      label="Is Other"
                      checked={formAnswer?.isOther}
                      onChange={(e) =>
                        setFormAnswer(
                          produce((draft) => {
                            draft!.isOther = e.target.checked;
                          })
                        )
                      }
                    />
                    {formAnswer?.isOther && (
                      <Select
                        required
                        label="Other Write-In Type"
                        value={viewResource?.formQuestionWriteInTypes?.find(
                          (x) => x.id === formAnswer?.otherWriteInTypeId
                        )}
                        options={viewResource?.formQuestionWriteInTypes}
                        getOptionLabel={(option) => option.name}
                        onChange={(e, value) =>
                          setFormAnswer(
                            produce((draft) => {
                              draft!.otherWriteInTypeId = value?.id;
                            })
                          )
                        }
                      />
                    )}
                    <GrabIconButton {...provided.dragHandleProps} />
                    <DeleteIconButton
                      onClick={(e) => {
                        deleteAnswer(e);
                      }}
                    />
                  </Stack>
                )}
              </Box>
            </Stack>
          </Box>
        </div>
      )}
    </Draggable>
  );
};
